import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, Clock, ChefHat, Package, XCircle, Loader2, CreditCard, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { OrderStatus, STATUS_LABELS, PAYMENT_METHOD_LABELS, PaymentMethod } from '@/types/order';
import GlutenFreeIcon from '@/components/GlutenFreeIcon';
import PageSEO from '@/components/PageSEO';

interface OrderItem {
  name_snapshot: string;
  quantity: number;
  gluten_option: 'con_glutine' | 'senza_glutine';
  line_total_cents: number;
}

interface OrderData {
  id: string;
  customer_name: string;
  status: OrderStatus;
  payment_method: PaymentMethod;
  is_paid: boolean;
  pickup_time: string | null;
  total_cents: number;
  created_at: string;
  order_note: string | null;
  items: OrderItem[];
  can_cancel: boolean;
}

// Display status labels for customer view (simplified)
const DISPLAY_STATUS_LABELS: Record<OrderStatus, string> = {
  'da_pagare': 'Ricevuto',
  'pagato': 'Ricevuto',
  'in_preparazione': 'In preparazione',
  'pronto': 'Pronto',
  'annullato': 'Annullato',
};

const STATUS_ICONS: Record<OrderStatus, React.ReactNode> = {
  'da_pagare': <Clock className="h-8 w-8" />,
  'pagato': <Clock className="h-8 w-8" />,
  'in_preparazione': <ChefHat className="h-8 w-8" />,
  'pronto': <Package className="h-8 w-8" />,
  'annullato': <XCircle className="h-8 w-8" />,
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  'da_pagare': 'text-blue-500 bg-blue-500/10',
  'pagato': 'text-blue-500 bg-blue-500/10',
  'in_preparazione': 'text-orange-500 bg-orange-500/10',
  'pronto': 'text-green-500 bg-green-500/10',
  'annullato': 'text-red-500 bg-red-500/10',
};

const OrderTracking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const success = searchParams.get('success');
  const canceled = searchParams.get('canceled');

  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (success === 'true') {
      toast.success('Pagamento completato con successo!');
    } else if (canceled === 'true') {
      toast.info('Pagamento annullato');
    }
  }, [success, canceled]);

  useEffect(() => {
    if (!token) {
      setError('Token ordine mancante');
      setLoading(false);
      return;
    }

    fetchOrder();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchOrder, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchOrder = async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-order-by-token?token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ordine non trovato');
      }

      const orderData = await response.json();
      setOrder(orderData);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching order:', err);
      setError(err.message || 'Errore nel recupero dell\'ordine');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!token || !order?.can_cancel) return;

    const confirmed = window.confirm('Sei sicuro di voler annullare l\'ordine?');
    if (!confirmed) return;

    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-order-by-token', {
        body: { token, reason: 'Annullato dal cliente' },
      });

      if (error) throw error;
      
      toast.success('Ordine annullato con successo');
      fetchOrder(); // Refresh order data
    } catch (err: any) {
      console.error('Error cancelling order:', err);
      toast.error(err.message || 'Errore nell\'annullamento dell\'ordine');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento ordine...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <XCircle className="h-10 w-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Ordine non trovato
          </h1>
          <p className="text-muted-foreground mb-8">
            {error || 'Impossibile trovare l\'ordine. Verifica il link ricevuto via email.'}
          </p>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alla Home
          </Button>
        </div>
      </div>
    );
  }

  const shortCode = token?.slice(-8).toUpperCase() || '';
  const pickupTime = order.pickup_time 
    ? new Date(order.pickup_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    : 'Da confermare';

  return (
    <div className="min-h-screen bg-background py-8">
      <PageSEO
        title="Stato Ordine – Pizzeria La Torre, Carmagnola (TO)"
        description="Segui lo stato del tuo ordine di pizza da asporto presso Pizzeria La Torre a Carmagnola (TO): ricevuto, in preparazione, pronto per il ritiro."
        noindex
      />
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="sr-only">Stato Ordine</h1>
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-5 w-5" />
          Torna al Menù
        </button>

        {/* Order Code */}
        <div className="card-elevated p-6 text-center mb-6">
          <p className="text-sm text-muted-foreground mb-2">Codice Ordine</p>
          <p className="font-mono text-3xl font-bold text-primary tracking-widest">{shortCode}</p>
        </div>

        {/* Status */}
        <div className="card-elevated p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${STATUS_COLORS[order.status]}`}>
              {STATUS_ICONS[order.status]}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Stato ordine</p>
              <p className="text-xl font-bold text-foreground">
                {DISPLAY_STATUS_LABELS[order.status]}
              </p>
            </div>
          </div>

          {/* Progress bar - based on preparation status, not payment */}
          {order.status !== 'annullato' && (
            <div className="mt-6">
              <div className="flex justify-between text-xs text-muted-foreground mb-2">
                <span>Ricevuto</span>
                <span>In preparazione</span>
                <span>Pronto</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-500"
                  style={{ 
                    width: (order.status === 'da_pagare' || order.status === 'pagato') ? '33%' 
                      : order.status === 'in_preparazione' ? '66%' 
                      : '100%' 
                  }}
                />
              </div>
            </div>
          )}

          {/* Payment Status - separate from progress */}
          {order.status !== 'annullato' && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-sm text-muted-foreground">Stato pagamento:</span>
              {order.is_paid ? (
                <span className="flex items-center gap-1 text-sm font-medium text-green-600 bg-green-500/10 px-2 py-1 rounded-full">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Pagato
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm font-medium text-yellow-600 bg-yellow-500/10 px-2 py-1 rounded-full">
                  <Clock className="h-3.5 w-3.5" />
                  Da pagare
                </span>
              )}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className="card-elevated p-6 mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Dettagli Ordine
          </h2>

          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cliente</span>
              <span className="font-medium text-foreground">{order.customer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Orario ritiro</span>
              <span className="font-medium text-foreground">{pickupTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Metodo pagamento</span>
              <span className="font-medium text-foreground flex items-center gap-1">
                {order.payment_method === 'stripe' ? (
                  <>
                    <CreditCard className="h-4 w-4" />
                    Online
                  </>
                ) : (
                  <>
                    <Wallet className="h-4 w-4" />
                    Al ritiro
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Items */}
          <div className="border-t border-border pt-4 space-y-3">
            {order.items.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-foreground">
                    {item.name_snapshot}
                    {item.gluten_option === 'senza_glutine' && (
                      <span className="inline-flex items-center gap-1 text-xs ml-2 px-1.5 py-0.5 rounded badge-gf">
                        <GlutenFreeIcon className="h-2.5 w-2.5" />
                        GF
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground">x{item.quantity}</span>
                </div>
                <span className="font-medium text-foreground">
                  €{(item.line_total_cents / 100).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="border-t border-border pt-4 mt-4">
            <div className="flex justify-between text-lg">
              <span className="font-semibold text-foreground">Totale</span>
              <span className="font-bold text-primary">
                €{(order.total_cents / 100).toFixed(2)}
              </span>
            </div>
          </div>

          {order.order_note && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Note:</span> {order.order_note}
              </p>
            </div>
          )}
        </div>

        {/* Cancel Button */}
        {order.can_cancel && (
          <Button
            variant="outline"
            className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={handleCancelOrder}
            disabled={cancelling}
          >
            {cancelling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Annullamento...
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 mr-2" />
                Annulla Ordine
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
